import { ITimeFrameObject } from './i-time-frame-object';
import { PlacementOnCanvas } from '../canvas/placement-on-canvas';
import ContentControlSubtitlesContainer from '../subtitles/content-control-subtitles-container';
import { ContentControlledEventActionEnum } from '../enums/content-controlled-event-action.enum';
import { ContentControlledMediaTypeEnum } from '../enums/content-controlled-media-type.enum';
import { ContentControlledKeyframe } from '../keyframe/content-controlled-keyframe';
import { ContentControlledEditingDirectiveViewModel } from './content-controlled-editing-directive-view-model';
import { ContentControlledEditingDirectiveInstructions } from './content-controlled-editing-directive-instructions';
import { ContentControlledEditingDirectiveScreenshot } from './content-controlled-editing-directive-screenshot';

export class ContentControlledEditingDirective implements ITimeFrameObject {
  private _parentId: number = 0;
  constructor(
    public id: number,
    public mediaType: ContentControlledMediaTypeEnum,
    public startsAtMs: number,
    public endsAtMs: number,
    public title: string,
    public isApproved: boolean,
    public isMitigated: boolean,
    public action: ContentControlledEventActionEnum,
    public keyframes: Array<ContentControlledKeyframe> = [],
    public startFrame?: string,
    public endFrame?: string,
    public instructions?: ContentControlledEditingDirectiveInstructions,
    public subtitles?: ContentControlSubtitlesContainer,
    public createdAt?: number,
    public mediaId?: number,
  ) {}
  public toView(slide: number) {
    return new ContentControlledEditingDirectiveViewModel(
      this.id,
      this.parentId,
      this.mediaType,
      this.startsAtMs + slide,
      this.endsAtMs + slide,
      this.title,
      this.isApproved,
      this.isMitigated,
      this.startFrame,
      this.endFrame,
      this.keyframes.map((x) => x.clone()),
      this.action,
      this.instructions ? this.instructions : undefined,
      this.subtitles?.clone() || undefined,
      this.createdAt,
      this.mediaId,
    );
  }
  public clone() {
    return new ContentControlledEditingDirective(
      this.id,
      this.mediaType,
      this.startsAtMs,
      this.endsAtMs,
      this.title,
      this.isApproved,
      this.isMitigated,
      this.action,
      this.keyframes.map((x) => x.clone()),
      this.startFrame,
      this.endFrame,
      this.instructions != null ? { ...this.instructions } : undefined,
      this.subtitles?.clone() ?? undefined,
      this.createdAt,
      this.mediaId,
    );
  }
  public get parentId() {
    return this._parentId;
  }
  public set parentId(pid: number) {
    this._parentId = pid;
  }
  public get duration() {
    return this.endsAtMs - this.startsAtMs;
  }
  public createMotionTween(currentTime: number) {
    let poc: PlacementOnCanvas;
    if (this.keyframes.some((x) => x.timeInMs === currentTime)) {
      return;
    } else {
      let startKeyframe: ContentControlledKeyframe;
      let endKeyframe: ContentControlledKeyframe;

      if (this.keyframes.length > 1) {
        if (this.keyframes.length == 2) {
          startKeyframe = this.keyframes[0];
          endKeyframe = this.keyframes[1];
          if (currentTime > endKeyframe.timeInMs) {
            startKeyframe = undefined;
          }
        } else {
          const kfIndex = this.keyframes.findIndex(
            (x) => x.timeInMs > currentTime,
          );
          if (kfIndex > -1) {
            startKeyframe =
              this.keyframes[kfIndex - 1] || this.keyframes[kfIndex];
            endKeyframe = this.keyframes[kfIndex];
          }
        }
        if (startKeyframe && endKeyframe) {
          const t =
            (currentTime - startKeyframe.timeInMs) /
            (endKeyframe.timeInMs - startKeyframe.timeInMs);
          poc = startKeyframe.interpolatePlacementOnCanvas(endKeyframe, t);
        }
      }
      poc = poc || this.keyframes[this.keyframes.length - 1].exportPlacement();
    }
    const x = poc && poc.x > -1 ? poc.x : 40;
    const y = poc && poc.y > -1 ? poc.y : 40;
    const w = poc && poc.width > -1 ? poc.width : 15;
    const h = poc && poc.height > -1 ? poc.height : 15;
    return new ContentControlledKeyframe(currentTime, x, y, w, h, true);
  }
  public sortKeyframes(keyframes?: Array<ContentControlledKeyframe>) {
    keyframes = keyframes || this.keyframes;
    keyframes.sort((a, b) => a.timeInMs - b.timeInMs);
  }

  public getScreenshotDirectives(): ContentControlledEditingDirectiveScreenshot[] {
    const result = [];

    if (
      this.action === ContentControlledEventActionEnum.VideoClip &&
      this.mediaId != null
    ) {
      const durationSec = (this.endsAtMs - this.startsAtMs) / 1000;
      if (!this.startFrame) {
        result.push(
          new ContentControlledEditingDirectiveScreenshot(
            this.id,
            'in',
            0,
            undefined,
            undefined,
            this.mediaId,
          ),
        );
      }
      if (!this.endFrame) {
        result.push(
          new ContentControlledEditingDirectiveScreenshot(
            this.id,
            'out',
            durationSec,
            undefined,
            undefined,
            this.mediaId,
          ),
        );
      }
      return result;
    }

    if (!this.startFrame) {
      result.push(
        new ContentControlledEditingDirectiveScreenshot(
          this.id,
          'in',
          this.startsAtMs / 1000,
        ),
      );
    }
    if (!this.endFrame) {
      result.push(
        new ContentControlledEditingDirectiveScreenshot(
          this.id,
          'out',
          this.endsAtMs / 1000,
        ),
      );
    }
    return result;
  }

  public getKeyframeByTime(currentTime: number, forVideoEditing = false) {
    if (this.keyframes.length == 0) {
      return null;
    }
    const keyframes = [...this.keyframes];
    this.sortKeyframes(keyframes);
    if (currentTime > -1) {
      const k = this.keyframes.findIndex((x) => currentTime == x.timeInMs);
      if (k === keyframes.length - 1) {
        return keyframes[keyframes.length - 1].clone();
      }
      if (k === -1 && forVideoEditing) {
        return this.createMotionTween(currentTime);
      }

      for (let i = 0; i < keyframes.length - 1; i++) {
        const startKeyframe = keyframes[i];
        const endKeyframe = keyframes[i + 1];
        if (
          currentTime >= startKeyframe.timeInMs &&
          currentTime < endKeyframe.timeInMs
        ) {
          const t =
            (currentTime - startKeyframe.timeInMs) /
            (endKeyframe.timeInMs - startKeyframe.timeInMs);
          if (startKeyframe.sameTime(endKeyframe)) {
            return startKeyframe.clone();
          } else {
            const { x, y, width, height } =
              startKeyframe.interpolatePlacementOnCanvas(endKeyframe, t);
            return new ContentControlledKeyframe(
              startKeyframe.timeInMs,
              x,
              y,
              width,
              height,
              true && !forVideoEditing,
            );
          }
        }
      }
    }
    return keyframes[keyframes.length - 1].clone();
  }

  public hasKeyframe(keyframe: ContentControlledKeyframe) {
    return this.keyframes.find((x) => x.sameTime(keyframe));
  }
  public isKeyframeTween(currentTime: number): boolean {
    const th = currentTime * 0.0000001;
    const kf = this.keyframes.find(
      (x) => x.timeInMs >= currentTime - th && x.timeInMs <= currentTime + th,
    );
    return kf ? true : false;
  }
  public equals(other: ContentControlledEditingDirective): boolean {
    return (
      this.id == other.id &&
      this.startsAtMs == other.startsAtMs &&
      this.endsAtMs == other.endsAtMs &&
      this.action == other.action &&
      this.instructions == other.instructions &&
      this.isMitigated == other.isMitigated &&
      this.isApproved == other.isApproved &&
      this.mediaType == other.mediaType &&
      this.mediaId === other.mediaId
    );
  }
}
